import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2, Gift, Tag, Percent } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRewardsPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [rewards, setRewards] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [rewardForm, setRewardForm] = useState({
    name: '', description: '', points_required: 100, category: 'Booking'
  });
  const [promoForm, setPromoForm] = useState({
    code: '', description: '', discount_type: 'percentage', discount_value: 10,
    valid_from: '', valid_until: '', max_uses: 100
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [rewardsRes, promosRes] = await Promise.all([
        fetch(`${API}/admin/rewards`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/admin/promo-codes`, { headers: { 'Authorization': `Bearer ${token}` }})
      ]);
      if (rewardsRes.ok) setRewards(await rewardsRes.json());
      if (promosRes.ok) setPromoCodes(await promosRes.json());
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const createReward = async () => {
    try {
      const res = await fetch(`${API}/admin/rewards`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(rewardForm)
      });
      if (res.ok) { toast.success('Reward created'); setShowRewardModal(false); fetchData(); }
    } catch (error) { toast.error('Failed'); }
  };

  const createPromo = async () => {
    try {
      const res = await fetch(`${API}/admin/promo-codes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(promoForm)
      });
      if (res.ok) { toast.success('Promo code created'); setShowPromoModal(false); fetchData(); }
    } catch (error) { toast.error('Failed'); }
  };

  const deleteReward = async (rewardId) => {
    if (!confirm('Delete?')) return;
    try {
      await fetch(`${API}/admin/rewards/${rewardId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
      toast.success('Deleted'); fetchData();
    } catch (error) { toast.error('Failed'); }
  };

  const deletePromo = async (promoId) => {
    if (!confirm('Delete?')) return;
    try {
      await fetch(`${API}/admin/promo-codes/${promoId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
      toast.success('Deleted'); fetchData();
    } catch (error) { toast.error('Failed'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className={`text-2xl font-bold ${theme.text}`}>Rewards & Promo Codes</h1>
          <p className={`${theme.textMuted} mt-1`}>Manage loyalty rewards and promotions</p>
        </div>

        {/* Rewards Section */}
        <Card className={theme.card}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={`${theme.text} flex items-center gap-2`}>
              <Gift className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} /> Loyalty Rewards
            </CardTitle>
            {hasRole(['super_admin', 'manager']) && (
              <Button size="sm" onClick={() => setShowRewardModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-1" /> Add Reward
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
            ) : rewards.length === 0 ? (
              <p className={`text-center py-4 ${theme.textMuted}`}>No rewards</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rewards.map((reward) => (
                  <div key={reward.reward_id} className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className={`font-medium ${theme.text}`}>{reward.name}</h3>
                      {hasRole(['super_admin', 'manager']) && (
                        <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={() => deleteReward(reward.reward_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className={`text-sm mb-2 ${theme.textMuted}`}>{reward.description}</p>
                    <div className="flex justify-between items-center">
                      <span className={`text-xs ${theme.textSubtle}`}>{reward.category}</span>
                      <span className={`font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{reward.points_required} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Promo Codes Section */}
        <Card className={theme.card}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={`${theme.text} flex items-center gap-2`}>
              <Tag className={`h-5 w-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} /> Promo Codes
            </CardTitle>
            {hasRole(['super_admin', 'manager']) && (
              <Button size="sm" onClick={() => setShowPromoModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="h-4 w-4 mr-1" /> Add Code
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {promoCodes.length === 0 ? (
              <p className={`text-center py-4 ${theme.textMuted}`}>No promo codes</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`text-left border-b ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
                      <th className="pb-3">Code</th>
                      <th className="pb-3">Discount</th>
                      <th className="pb-3">Valid Until</th>
                      <th className="pb-3">Uses</th>
                      <th className="pb-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((promo) => (
                      <tr key={promo.promo_id} className={`border-b ${isDarkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
                        <td className={`py-3 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{promo.code}</td>
                        <td className={`py-3 ${theme.text}`}>
                          {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `₱${promo.discount_value}`}
                        </td>
                        <td className={`py-3 ${theme.textMuted}`}>{promo.valid_until || 'No expiry'}</td>
                        <td className={`py-3 ${theme.textMuted}`}>{promo.times_used || 0} / {promo.max_uses}</td>
                        <td className="py-3">
                          {hasRole(['super_admin', 'manager']) && (
                            <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={() => deletePromo(promo.promo_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reward Modal */}
        {showRewardModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.modalBg}`}>
              <CardHeader><CardTitle className={theme.text}>Add Reward</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Name</Label>
                  <Input value={rewardForm.name} onChange={(e) => setRewardForm(p => ({ ...p, name: e.target.value }))} className={theme.input} />
                </div>
                <div>
                  <Label className={theme.text}>Description</Label>
                  <Input value={rewardForm.description} onChange={(e) => setRewardForm(p => ({ ...p, description: e.target.value }))} className={theme.input} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Points Required</Label>
                    <Input type="number" value={rewardForm.points_required} onChange={(e) => setRewardForm(p => ({ ...p, points_required: parseInt(e.target.value) }))} className={theme.input} />
                  </div>
                  <div>
                    <Label className={theme.text}>Category</Label>
                    <select value={rewardForm.category} onChange={(e) => setRewardForm(p => ({ ...p, category: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                      <option>Booking</option><option>Equipment</option><option>Refreshments</option><option>Coaching</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowRewardModal(false)}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={createReward}>Create</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Promo Modal */}
        {showPromoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.modalBg}`}>
              <CardHeader><CardTitle className={theme.text}>Add Promo Code</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Code</Label>
                  <Input value={promoForm.code} onChange={(e) => setPromoForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} className={`${theme.input} font-mono`} placeholder="SUMMER2025" />
                </div>
                <div>
                  <Label className={theme.text}>Description</Label>
                  <Input value={promoForm.description} onChange={(e) => setPromoForm(p => ({ ...p, description: e.target.value }))} className={theme.input} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Discount Type</Label>
                    <select value={promoForm.discount_type} onChange={(e) => setPromoForm(p => ({ ...p, discount_type: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <Label className={theme.text}>Value</Label>
                    <Input type="number" value={promoForm.discount_value} onChange={(e) => setPromoForm(p => ({ ...p, discount_value: parseFloat(e.target.value) }))} className={theme.input} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Valid Until</Label>
                    <Input type="date" value={promoForm.valid_until} onChange={(e) => setPromoForm(p => ({ ...p, valid_until: e.target.value }))} className={theme.input} />
                  </div>
                  <div>
                    <Label className={theme.text}>Max Uses</Label>
                    <Input type="number" value={promoForm.max_uses} onChange={(e) => setPromoForm(p => ({ ...p, max_uses: parseInt(e.target.value) }))} className={theme.input} />
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowPromoModal(false)}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={createPromo}>Create</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRewardsPage;
